import type { ComponentType } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { LifeBuoy } from 'lucide-react'
import { useMemo } from 'react'
import { organisationsQueryOptions } from '@/api/resources/organisations'
import { BoxIcon } from '@/components/icons/box-icon'
import { BuildingIcon } from '@/components/icons/building-icon'
import { BulbIcon } from '@/components/icons/bulb-icon'
import { ChartIcon } from '@/components/icons/chart-icon'
import { HistoryIcon } from '@/components/icons/history-icon'
import { JuneaIcon } from '@/components/icons/junea-icon'
import { PlugIcon } from '@/components/icons/plug-icon'
import { ReceiptIcon } from '@/components/icons/receipt-icon'
import { UserIcon } from '@/components/icons/user-icon'
import { WalletIcon } from '@/components/icons/wallet-icon'
import { JuneaLogo } from '@/components/junea-logo'
import { JuneaLogoSmall } from '@/components/junea-logo-small'
import { BotpressChat } from '@/components/layout/botpress-chat'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar'

interface NavItem {
  label: string
  icon: ComponentType<{ className?: string }>
  href: string
  exact?: boolean
}

const staticNavItems: NavItem[] = [{ label: 'Dashboard', icon: ChartIcon, href: '/', exact: true }]

const afterOrgNavItems: NavItem[] = [
  { label: 'Machines', icon: JuneaIcon, href: '/machines' },
  { label: 'Insights', icon: BulbIcon, href: '/insights' },
]

function NavGroup({
  items,
  onNavigate,
}: {
  items: NavItem[]
  onNavigate: () => void
}): React.ReactNode {
  return (
    <SidebarGroup className="px-3 py-0">
      <SidebarGroupContent>
        <SidebarMenu className="gap-2.5">
          {items.map((item) => (
            <SidebarMenuItem key={item.label}>
              <SidebarMenuButton
                asChild
                tooltip={item.label}
                className="h-8 gap-4 rounded-sm px-2.5 text-xs"
              >
                <Link
                  to={item.href}
                  activeOptions={{ exact: item.exact ?? false }}
                  activeProps={{
                    className: 'bg-sidebar-accent text-sidebar-accent-foreground font-bold',
                  }}
                  onClick={onNavigate}
                >
                  <item.icon className="size-5" />
                  <span>{item.label}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

export function AppSidebar(): React.ReactNode {
  const { setOpenMobile } = useSidebar()
  const { data: organisations } = useQuery(organisationsQueryOptions())

  const singleOrganisation = organisations?.length === 1 ? organisations[0] : undefined
  const organisationsHref =
    singleOrganisation !== undefined ? `/organisations/${singleOrganisation.id}` : '/organisations'

  const mainNavItems = useMemo<NavItem[]>(
    () => [
      ...staticNavItems,
      { label: 'Organisations', icon: BuildingIcon, href: organisationsHref },
      ...afterOrgNavItems,
    ],
    [organisationsHref],
  )

  const walletOrganisations = useMemo(
    () => organisations?.filter((organisation) => organisation.company_wallet_enabled) ?? [],
    [organisations],
  )

  const computedSecondaryNavItems = useMemo<NavItem[]>(() => {
    const productsHref =
      singleOrganisation !== undefined ? `/products/${singleOrganisation.id}` : '/products'
    const usersHref =
      singleOrganisation !== undefined
        ? `/users/${singleOrganisation.id}/system-managers`
        : '/users'
    const singleWalletOrganisation =
      walletOrganisations.length === 1 ? walletOrganisations[0] : undefined
    const walletsHref =
      singleWalletOrganisation !== undefined
        ? `/wallets/${singleWalletOrganisation.id}`
        : '/wallets'

    return [
      { label: 'Products', icon: BoxIcon, href: productsHref },
      { label: 'Invoices', icon: ReceiptIcon, href: '/invoices' },
      { label: 'Orders', icon: HistoryIcon, href: '/orders' },
      { label: 'Users', icon: UserIcon, href: usersHref },
      ...(walletOrganisations.length > 0
        ? [{ label: 'Wallets', icon: WalletIcon, href: walletsHref }]
        : []),
      { label: 'Integrations', icon: PlugIcon, href: '/integrations' },
    ]
  }, [singleOrganisation, walletOrganisations])

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-5 py-5">
        <Link to="/" className="flex items-center justify-center">
          <JuneaLogo className="w-28 text-white group-data-[collapsible=icon]:hidden" />
          <JuneaLogoSmall className="hidden h-7 group-data-[collapsible=icon]:block" />
        </Link>
      </SidebarHeader>

      <SidebarContent className="gap-0">
        <NavGroup items={mainNavItems} onNavigate={() => setOpenMobile(false)} />
        <SidebarSeparator className="my-2.5" />
        <NavGroup items={computedSecondaryNavItems} onNavigate={() => setOpenMobile(false)} />
      </SidebarContent>

      <SidebarFooter className="px-3 pb-4">
        <SidebarSeparator className="mb-2.5" />
        <SidebarMenu>
          <BotpressChat />
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Support"
              className="text-sidebar-foreground/50 hover:text-sidebar-foreground/80 h-8 gap-4 rounded-sm px-2.5 text-xs"
            >
              <a href="https://support.junea.nl" target="_blank" rel="noopener noreferrer">
                <LifeBuoy className="size-5" />
                <span>Support</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
